import { Pause, Play, RotateCcw } from "lucide-react"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { ReviewScrubber, ReviewVideoPlayer, type ScrubSource } from "@/components/review/review-video-player"
import { type TimelinePlayerPlaybackApi } from "@/components/timeline-player"
import { Button } from "@/components/ui/primitives"
import { useTranslation } from "@/hooks/use-translation"

export type CompareMode = "side" | "wipe"

/** Версия для просмотра и сравнения: кадр / видео шота или изображение ассета. */
export type ReviewMediaVersion = {
  id: number
  number: number
  /** Подпись вместо «версия N»: имя версии видео, «Оригинал», вид обработки. */
  label?: string | null
  kind: string
  active: boolean
  media_url: string | null
  thumb_url: string | null
  duration_ms?: number | null
}

const FRAME_CLASS = "relative overflow-hidden rounded-md bg-ui-surface-sunken"

function ratioOf(frameFormat: string | null): number {
  const [w, h] = (frameFormat ?? "16:9").split(":").map(Number)
  return w > 0 && h > 0 ? w / h : 16 / 9
}

function aspectStyle(frameFormat: string | null) {
  return { aspectRatio: String(ratioOf(frameFormat)) }
}

const isVideo = (version: ReviewMediaVersion) => version.kind === "video"

function VersionBadge({ version, side = "left" }: { version: ReviewMediaVersion; side?: "left" | "right" }) {
  const { t } = useTranslation("projects")
  return (
    <span
      className={`pointer-events-none absolute top-2 z-10 rounded-sm bg-black/60 px-1.5 py-0.5 text-[11px] text-white ${
        side === "left" ? "left-2" : "right-2"
      }`}
    >
      {version.label || t("review.versionBadge", { number: version.number })}
      {version.active ? ` · ${t("review.current")}` : ""}
    </span>
  )
}

/** Видео версии: плеер страницы шота и минималистичная дорожка (ReviewVideoPlayer). */
function VersionPlayer({
  version,
  frameFormat,
  onPlaybackReady,
}: {
  version: ReviewMediaVersion
  frameFormat: string | null
  onPlaybackReady?: (api: TimelinePlayerPlaybackApi | null) => void
}) {
  if (!version.media_url) return null
  return (
    <div className="relative">
      <VersionBadge version={version} />
      <ReviewVideoPlayer
        key={version.id}
        sourceId={version.id}
        streamUrl={version.media_url}
        durationMs={version.duration_ms}
        aspectRatio={ratioOf(frameFormat)}
        onPlaybackReady={onPlaybackReady}
      />
    </div>
  )
}

/** Одна версия крупно (R1): кадр картинкой, видео — плеером страницы шота. */
export function ShotReviewViewer({
  version,
  frameFormat,
}: {
  version: ReviewMediaVersion
  frameFormat: string | null
}) {
  if (isVideo(version)) return <VersionPlayer version={version} frameFormat={frameFormat} />
  return (
    <div className={FRAME_CLASS} style={aspectStyle(frameFormat)}>
      <VersionBadge version={version} />
      {version.media_url ? (
        <img src={version.media_url} alt="" className="absolute inset-0 h-full w-full object-contain" />
      ) : null}
    </div>
  )
}

/** Общее управление двумя роликами сравнения: ставит их в одну точку и запускает или останавливает вместе. */
type SyncedClip = { play: () => void; pause: () => void; seek: (ms: number) => void; timeMs: () => number }

/** Квадратная кнопка «Играть обе» и «С начала» под ней: между таймлайнами «Рядом» и под таймлайном шторки. */
function SharedTransport({ clips }: { clips: () => SyncedClip[] }) {
  const { t } = useTranslation("projects")
  const [playing, setPlaying] = useState(false)
  const toggle = () => {
    const list = clips()
    if (list.length === 0) return
    if (playing) {
      list.forEach((clip) => clip.pause())
      setPlaying(false)
      return
    }
    const at = list[0].timeMs()
    list.forEach((clip) => {
      clip.seek(at)
      clip.play()
    })
    setPlaying(true)
  }
  const restart = () => {
    clips().forEach((clip) => {
      clip.seek(0)
      clip.play()
    })
    setPlaying(true)
  }
  const label = playing ? t("review.compare.pauseBoth") : t("review.compare.playBoth")
  return (
    <div className="flex flex-col items-center gap-0.5">
      <Button
        size="icon"
        variant="secondary"
        className="h-8 w-8 rounded-md"
        onClick={toggle}
        aria-label={label}
        title={label}
      >
        {playing ? <Pause className="h-3.5 w-3.5" aria-hidden /> : <Play className="h-3.5 w-3.5" aria-hidden />}
      </Button>
      <button
        type="button"
        onClick={restart}
        aria-label={t("review.compare.fromStart")}
        title={t("review.compare.fromStart")}
        className="flex h-6 w-8 items-center justify-center text-ui-text-muted hover:text-ui-text-primary"
      >
        <RotateCcw className="h-3.5 w-3.5" aria-hidden />
      </button>
    </div>
  )
}

/**
 * Дорожка шторки: время ведёт левый ролик, перемотка ставит в одну точку оба. Время — снимок, обновляемый в кадре
 * анимации и по событиям ролика: useSyncExternalStore требует, чтобы два чтения подряд совпадали, а живой
 * currentTime у играющего ролика меняется между ними — React уходил в бесконечный рендер (#185).
 */
function wipeScrubSource(master: HTMLVideoElement, all: () => (HTMLVideoElement | null)[]): ScrubSource {
  let snapshot = master.currentTime * 1000
  return {
    subscribe: (listener) => {
      let frame = 0
      const update = () => {
        const next = master.currentTime * 1000
        if (next === snapshot) return
        snapshot = next
        listener()
      }
      const tick = () => {
        update()
        frame = requestAnimationFrame(tick)
      }
      const start = () => {
        cancelAnimationFrame(frame)
        frame = requestAnimationFrame(tick)
      }
      const stop = () => {
        cancelAnimationFrame(frame)
        update()
      }
      master.addEventListener("play", start)
      master.addEventListener("pause", stop)
      master.addEventListener("seeked", update)
      if (!master.paused) start()
      return () => {
        cancelAnimationFrame(frame)
        master.removeEventListener("play", start)
        master.removeEventListener("pause", stop)
        master.removeEventListener("seeked", update)
      }
    },
    timeMs: () => snapshot,
    scrubTo: (ms) =>
      all().forEach((el) => {
        if (el) el.currentTime = ms / 1000
      }),
    endScrub: () => undefined,
  }
}

/**
 * Сравнение двух версий — одно на панели проверки, просмотре холста и вкладках версий шота. Кадры — рядом или
 * шторкой. Видео рядом — в плеерах страницы шота, общая кнопка запуска между их таймлайнами. Шторка — размером
 * с обычный просмотр одной версии (не выше плеера), с одной дорожкой и той же кнопкой под ней.
 */
export function VersionCompare({
  a,
  b,
  mode,
  frameFormat,
}: {
  a: ReviewMediaVersion
  b: ReviewMediaVersion
  mode: CompareMode
  frameFormat: string | null
}) {
  const { t } = useTranslation("projects")
  const [wipe, setWipe] = useState(50)
  const players = useRef<(TimelinePlayerPlaybackApi | null)[]>([null, null])
  const videos = useRef<(HTMLVideoElement | null)[]>([null, null])
  const [master, setMaster] = useState<HTMLVideoElement | null>(null)
  const [masterMs, setMasterMs] = useState<number | null>(null)
  const video = isVideo(a)
  const ratio = ratioOf(frameFormat)

  const setVideoA = useCallback((el: HTMLVideoElement | null) => {
    videos.current[0] = el
    setMaster(el)
  }, [])
  const setVideoB = useCallback((el: HTMLVideoElement | null) => {
    videos.current[1] = el
  }, [])

  useEffect(() => {
    if (!master) return
    const read = () => {
      if (Number.isFinite(master.duration) && master.duration > 0) setMasterMs(master.duration * 1000)
    }
    read()
    master.addEventListener("loadedmetadata", read)
    return () => master.removeEventListener("loadedmetadata", read)
  }, [master])
  const wipeSource = useMemo(() => (master ? wipeScrubSource(master, () => videos.current) : null), [master])
  const wipeMs = a.duration_ms || masterMs

  const clips = useCallback((): SyncedClip[] => {
    if (mode === "side") {
      return players.current.flatMap((api) =>
        api
          ? [
              {
                play: () => api.controls.play(),
                pause: () => api.controls.pause(),
                seek: (ms: number) => api.controls.seek(ms),
                timeMs: () => api.timeStore.get(),
              },
            ]
          : [],
      )
    }
    return videos.current.flatMap((el) =>
      el
        ? [
            {
              play: () => void el.play(),
              pause: () => el.pause(),
              seek: (ms: number) => {
                el.currentTime = ms / 1000
              },
              timeMs: () => el.currentTime * 1000,
            },
          ]
        : [],
    )
  }, [mode])

  const media = (version: ReviewMediaVersion, slot: number) => {
    if (!version.media_url) return null
    if (isVideo(version)) {
      return (
        <video
          ref={slot === 0 ? setVideoA : setVideoB}
          src={version.media_url}
          poster={version.thumb_url ?? undefined}
          playsInline
          loop
          muted={slot === 1}
          className="absolute inset-0 h-full w-full object-contain"
        />
      )
    }
    return <img src={version.media_url} alt="" className="absolute inset-0 h-full w-full object-contain" />
  }

  if (mode === "side") {
    return video ? (
      <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-end gap-x-1.5">
        <VersionPlayer
          version={a}
          frameFormat={frameFormat}
          onPlaybackReady={(api) => {
            players.current[0] = api
          }}
        />
        <SharedTransport clips={clips} />
        <VersionPlayer
          version={b}
          frameFormat={frameFormat}
          onPlaybackReady={(api) => {
            players.current[1] = api
          }}
        />
      </div>
    ) : (
      <div className="grid grid-cols-2 gap-2">
        {[a, b].map((version, i) => (
          <div key={version.id} className={FRAME_CLASS} style={aspectStyle(frameFormat)}>
            <VersionBadge version={version} />
            {media(version, i)}
          </div>
        ))}
      </div>
    )
  }

  // Ширина шторки — по формату, но не выше плеера одной версии (TimelinePlayer: max-h min(60vh,520px)).
  const wipeWidth = `min(100%, calc(min(60vh, 520px) * ${ratio}))`
  return (
    <div className="mx-auto flex flex-col gap-1" style={{ width: wipeWidth }}>
      <div className={FRAME_CLASS} style={aspectStyle(frameFormat)}>
        {media(b, 1)}
        <div className="absolute inset-0" style={{ clipPath: `inset(0 ${100 - wipe}% 0 0)` }}>
          {media(a, 0)}
        </div>
        <span aria-hidden className="absolute inset-y-0 z-10 w-px bg-white/80" style={{ left: `${wipe}%` }} />
        <VersionBadge version={{ ...a, active: false }} />
        <VersionBadge version={{ ...b, active: false }} side="right" />
        <input
          type="range"
          min={0}
          max={100}
          value={wipe}
          onChange={(e) => setWipe(Number(e.target.value))}
          aria-label={t("review.compare.wipe")}
          className="absolute inset-0 z-20 h-full w-full cursor-ew-resize opacity-0"
        />
      </div>
      {video ? (
        <>
          {wipeMs ? <ReviewScrubber source={wipeSource} durationMs={wipeMs} /> : null}
          <SharedTransport clips={clips} />
        </>
      ) : null}
    </div>
  )
}
